import { useCallback, useMemo, useState } from 'react';
import { BadgesConfig } from '@app/types/pool';
import {
  getMarketMakerCampaign,
  getMetromMarketMakerCampaign
} from '@app/hooks/merkl/useGetMarketMakerCampaign';
import { usePoolsApr } from '@app/hooks/v3/usePoolsApr';
import { getTokensWhitelist } from '@app/constants/tokensWhitelist';
import { useVerifiedPoolsListV3 } from '@app/hooks/pools/useVerifiedPoolsListV3';
import { useAddress } from '@thirdweb-dev/react';
import { useConfig } from '@app/config';
import { useLocalStorageParam } from '@app/hooks/other/useLocalStorageParam';
import { useMarketMakerPools } from '@app/hooks/merkl/useMarketMakerPools';
import { useVerifiedPoolsList } from '@app/hooks/pools/useVerifiedPoolsList';
import { useTokenPriceV3 } from '@app/hooks/token/useTokenPriceV3';
import { usePairsDayData } from '@app/hooks/pair/usePairsDayData';
import { useVaults } from '@app/hooks/plugins/useVaults';
import { useIchiVaultsList } from '@app/hooks/ichi/useIchiVaultsList';
import { Campaign } from '@app/hooks/merkl/useMerklCampaigns';
import { MetromCampaign } from '@app/hooks/metrom/useMetromCampaigns';
import { useGammaVaults } from '@app/hooks/gamma/useGammaVaults';

interface Sort {
  column: string;
  dir: 'ASC' | 'DESC';
}

export interface PreparedListItem {
  view: 'v4' | 'v2' | 'vaults' | 'all';
  vaultsView?: 'all' | 'ichi' | 'steer' | 'gamma';
  data: any;
  sorting: Record<string, string | number>;
}

export function usePreparedPoolsRows(
  token: string = '',
  ignoreFilters = false
) {
  const address = useAddress();
  const config = useConfig();
  const TOKENS_WHITELIST = getTokensWhitelist(config);
  const [showUnverified, setShowUnverified] = useLocalStorageParam<string>(
    'KIM_POOLS_SHOW_UNVERIFIED',
    '0'
  );
  const [view, setView] = useLocalStorageParam<string>('KIM_POOLS_VIEW', 'all');
  const [vaultsView, setVaultsView] = useLocalStorageParam<string>(
    'KIM_POOLS_VAULTS_VIEW',
    'all'
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useLocalStorageParam<Sort>('poolsSort', {
    column: 'marketMaker',
    dir: 'DESC'
  });

  const { data: dataV2 } = useVerifiedPoolsList({
    tokens:
      showUnverified === '1'
        ? ''
        : TOKENS_WHITELIST.map(t => t.contractAddress.toLowerCase())
            .map(token => {
              return `"${token.toLowerCase()}"`;
            })
            .join(','),
    address: address ? address.toLowerCase() : '',
    orderBy: 'created',
    orderDirection: 'desc',
    search: searchTerm
  });
  const { data, status } = useVerifiedPoolsListV3({
    tokens:
      showUnverified === '1'
        ? ''
        : TOKENS_WHITELIST.map(t => t.contractAddress.toLowerCase())
            .map(token => {
              return `"${token.toLowerCase()}"`;
            })
            .join(','),
    address: address ? address.toLowerCase() : '',
    orderBy: 'created',
    orderDirection: 'desc',
    search: searchTerm || token
  });
  const { data: poolsApr } = usePoolsApr();

  const { data: steerVaults } = useVaults();
  const { data: ichiVaults } = useIchiVaultsList();
  const { data: gammaVaults } = useGammaVaults();

  const marketMakerPools = useMarketMakerPools();

  const { data: ethPrice } = useTokenPriceV3({
    id: 'weth'
  });

  const ids = data
    ? data
        .map(pair => {
          return `"${pair.id}"`;
        })
        .join(',')
    : undefined;

  const { data: pairsDayData } = usePairsDayData({
    ids
  });

  const preparedListV2: PreparedListItem[] | undefined = useMemo(() => {
    return dataV2?.map(item => {
      const dayData = pairsDayData?.filter(p => p.pairAddress === item.id);

      const badgesView: BadgesConfig | undefined =
        config?.FLAGS.showBadges?.find(
          (b: BadgesConfig) =>
            b.contractAddress.toLowerCase() === item?.id.toLowerCase()
        );

      return {
        view: 'v2',
        data: {
          ...item,
          dayData,
          ethPrice,
          badgesView
        },
        sorting: {
          name: `${item.token0.symbol}-${item.token1.symbol}`,
          apr: 0,
          tvl: 0,
          marketMaker: 0
        } as Record<string, string | number>
      };
    });
  }, [config?.FLAGS.showBadges, dataV2, ethPrice, pairsDayData]);

  const preparedListV4: PreparedListItem[] | undefined = useMemo(() => {
    return data?.map(item => {
      const badgesConfig: BadgesConfig | undefined =
        config?.FLAGS.showBadges?.find(
          (b: BadgesConfig) =>
            b.contractAddress.toLowerCase() === item?.id.toLowerCase()
        );

      const apr = poolsApr ? poolsApr[item.id] : undefined;

      const { marketMaker, marketMakerSortValue } = getMarketMakerParams(
        marketMakerPools,
        item.token0.id,
        item.token1.id
      );

      return {
        view: 'v4' as const,
        data: {
          pool: item,
          badgesConfig,
          apr,
          marketMaker
        },
        sorting: {
          name: `${item.token0.symbol}-${item.token1.symbol}`,
          apr: apr ? +apr : 0,
          tvl: item.totalValueLockedUSD ? +item.totalValueLockedUSD : 0,
          marketMaker: marketMakerSortValue
        } as Record<string, string | number>
      };
    });
  }, [config?.FLAGS.showBadges, data, marketMakerPools, poolsApr]);

  const preparedListVaults: PreparedListItem[] | undefined = useMemo(() => {
    const _steerVaults =
      steerVaults?.map(item => {
        const { marketMaker, marketMakerSortValue } = getMarketMakerParams(
          marketMakerPools,
          item.token0.id,
          item.token1.id
          // item.lpToken.id
        );

        return {
          view: 'vaults' as const,
          vaultsView: 'steer' as const,
          data: {
            ...item,
            id: item.lpToken.id,
            provider: 'Steer',
            marketMaker
          },
          sorting: {
            name: `${item.token0.symbol}-${item.token1.symbol}`,
            apr: item.apr ? +item.apr : 0,
            tvl: item.tvl ? +item.tvl : 0,
            marketMaker: marketMakerSortValue
          } as Record<string, string | number>
        };
      }) ?? [];
    const _ichiVaults =
      ichiVaults?.map(item => {
        const { marketMaker, marketMakerSortValue } = getMarketMakerParams(
          marketMakerPools,
          item.token0.id,
          item.token1.id
          // item.id
        );

        return {
          view: 'vaults' as const,
          vaultsView: 'ichi' as const,
          data: {
            ...item,
            provider: 'Ichi',
            name: 'SSD',
            marketMaker
          },
          sorting: {
            name: `${item.token0.symbol}-${item.token1.symbol}`,
            apr: item.apr ? +item.apr : 0,
            tvl: item.tvl ? +item.tvl : 0,
            marketMaker: marketMakerSortValue
          } as Record<string, string | number>
        };
      }) ?? [];
    const _gammaVaults =
      gammaVaults?.map(item => {
        const { marketMaker, marketMakerSortValue } = getMarketMakerParams(
          marketMakerPools,
          item.token0.id,
          item.token1.id
          // item.lpToken.id
        );

        return {
          view: 'vaults' as const,
          vaultsView: 'gamma' as const,
          data: {
            ...item,
            id: item.lpToken.id,
            provider: 'Gamma',
            marketMaker
          },
          sorting: {
            name: `${item.token0.symbol}-${item.token1.symbol}`,
            apr: item.apr ? +item.apr : 0,
            tvl: item.tvl ? +item.tvl : 0,
            marketMaker: marketMakerSortValue
          } as Record<string, string | number>
        };
      }) ?? [];

    return [..._ichiVaults, ..._steerVaults, ..._gammaVaults].filter(item => {
      return item.sorting.name
        .toString()
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    });
  }, [gammaVaults, ichiVaults, marketMakerPools, searchTerm, steerVaults]);

  const preparedList = useMemo(() => {
    return [
      ...(preparedListV4 ?? []),
      ...(preparedListV2 ?? []),
      ...(preparedListVaults ?? [])
    ]
      .filter(item => {
        if (ignoreFilters) {
          return true;
        }

        switch (view) {
          case 'v4': {
            return item.view === 'v4';
          }
          case 'v2': {
            return item.view === 'v2';
          }
          case 'vaults': {
            switch (vaultsView) {
              case 'ichi': {
                return item.view === 'vaults' && item.vaultsView === 'ichi';
              }
              case 'steer': {
                return item.view === 'vaults' && item.vaultsView === 'steer';
              }
              case 'gamma': {
                return item.view === 'vaults' && item.vaultsView === 'gamma';
              }
              default: {
                return item.view === 'vaults';
              }
            }
          }
          default: {
            return true;
          }
        }
      })
      .sort((a, b) => {
        if (a.sorting[sort.column] > b.sorting[sort.column]) {
          return sort.dir === 'ASC' ? 1 : -1;
        }

        if (a.sorting[sort.column] < b.sorting[sort.column]) {
          return sort.dir === 'ASC' ? -1 : 1;
        }

        return 0;
      });
  }, [
    ignoreFilters,
    preparedListV2,
    preparedListV4,
    preparedListVaults,
    sort.column,
    sort.dir,
    vaultsView,
    view
  ]);

  const handleSort = useCallback(
    (name: string) => {
      setSort({
        ...sort,
        column: name,
        dir: sort.dir === 'ASC' ? 'DESC' : 'ASC'
      });
    },
    [setSort, sort]
  );

  return {
    preparedList,
    handleSort,
    sort,
    status,
    setSearchTerm,
    view,
    vaultsView,
    setView,
    setVaultsView,
    showUnverified,
    setShowUnverified
  };
}

export function getMarketMakerParams(
  marketMakerPools: {
    merkl: (Campaign & { amountUSD: number })[];
    metrom: (MetromCampaign & { amountUSD: number })[];
  },
  token0: string | undefined,
  token1: string | undefined
) {
  const merkl = getMarketMakerCampaign(marketMakerPools.merkl, token0, token1);

  const metrom = getMetromMarketMakerCampaign(
    marketMakerPools.metrom,
    token0,
    token1
  );

  return {
    marketMaker: { merkl, metrom },
    marketMakerSortValue: merkl?.length || metrom?.length ? 1 : 0
  };
}

export function getV4MarketMakerParams(
  marketMakerPools: {
    merkl: (Campaign & { amountUSD: number })[];
    metrom: (MetromCampaign & { amountUSD: number })[];
  },
  token0: string | undefined,
  token1: string | undefined
) {
  const _merkl = getMarketMakerCampaign(marketMakerPools.merkl, token0, token1);
  const merkl = _merkl.filter(c => c.campaignParameters.whitelist.length === 0);

  const metrom = getMetromMarketMakerCampaign(
    marketMakerPools.metrom,
    token0,
    token1
  );

  return {
    marketMaker: { merkl, metrom },
    marketMakerSortValue: merkl?.length || metrom?.length ? 1 : 0
  };
}

export function getVaultMarketMakerParams(
  marketMakerPools: {
    merkl: (Campaign & { amountUSD: number })[];
    metrom: (MetromCampaign & { amountUSD: number })[];
  },
  token0: string | undefined,
  token1: string | undefined,
  vaultAddress: string | undefined
) {
  const _merkl = getMarketMakerCampaign(marketMakerPools.merkl, token0, token1);
  const merkl = vaultAddress
    ? _merkl.filter(c =>
        c.campaignParameters.whitelist
          .map(wa => wa.toLowerCase())
          .includes(vaultAddress.toLowerCase())
      )
    : [];

  const metrom = getMetromMarketMakerCampaign(
    marketMakerPools.metrom,
    token0,
    token1
  );

  return {
    marketMaker: { merkl, metrom },
    marketMakerSortValue: merkl?.length || metrom?.length ? 1 : 0
  };
}
